/**
 * NetzwerkPage - Personen- und Institutionen-Netzwerk
 * Zentrale Datenbank aller juristischen und natürlichen Personen,
 * komplex vernetzt mit allen Betreuten.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Network,
  Building2,
  UserCircle,
  Users,
  Search,
  RefreshCw,
  ChevronRight,
  Link2,
  Plus,
  ArrowUpRight,
  Download,
  Filter,
  X,
  Eye,
  Briefcase,
  Heart,
  Stethoscope,
  Scale,
  Banknote,
  Home,
  Phone,
  Mail,
  MapPin,
  Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import NetzwerkGraph from '@/components/NetzwerkGraph';

import { useTabs } from '@/contexts/TabContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Beziehungsarten Icons
const BEZIEHUNG_ICONS = {
  versichert_bei: Heart,
  leistung_von: Banknote,
  schulden_bei: Scale,
  wohnt_in: Home,
  arbeitet_bei: Briefcase,
  betreut_von: Users,
  bank_bei: Banknote,
  vermieter: Home,
  angehoerig: Users,
  behandelt_von: Stethoscope,
  rechtspfleger_fuer: Scale,
  sachbearbeiter_fuer: Briefcase,
  pflegekraft_fuer: Heart,
  ansprechpartner_fuer: Phone,
  sonstiges: Link2,
};

// Beziehungsarten Farben
const BEZIEHUNG_FARBEN = {
  versichert_bei: 'bg-pink-100 text-pink-700',
  leistung_von: 'bg-green-100 text-green-700',
  schulden_bei: 'bg-red-100 text-red-700',
  wohnt_in: 'bg-blue-100 text-blue-700',
  arbeitet_bei: 'bg-orange-100 text-orange-700',
  betreut_von: 'bg-purple-100 text-purple-700',
  bank_bei: 'bg-yellow-100 text-yellow-700',
  vermieter: 'bg-indigo-100 text-indigo-700',
  angehoerig: 'bg-rose-100 text-rose-700',
  behandelt_von: 'bg-teal-100 text-teal-700',
  rechtspfleger_fuer: 'bg-slate-100 text-slate-700',
  sachbearbeiter_fuer: 'bg-amber-100 text-amber-700',
  pflegekraft_fuer: 'bg-cyan-100 text-cyan-700',
  ansprechpartner_fuer: 'bg-lime-100 text-lime-700',
  sonstiges: 'bg-gray-100 text-gray-700',
};

export default function NetzwerkPage() {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('suche');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTypFilter, setSearchTypFilter] = useState('alle');
  const [searchResults, setSearchResults] = useState([]);
  
  // Detail-Ansichten
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Statistiken
  const [stats, setStats] = useState(null);
  
  // Neue Verknüpfung Dialog
  const [showNewLinkDialog, setShowNewLinkDialog] = useState(false);
  const [newLink, setNewLink] = useState({
    quell_typ: '',
    quell_id: '',
    ziel_typ: '',
    ziel_id: '',
    beziehung_art: '',
    beziehung_details: ''
  });
  
  // Beziehungsarten
  const [beziehungsarten, setBeziehungsarten] = useState([]);

  // URL-Parameter verarbeiten
  useEffect(() => {
    const typ = searchParams.get('typ');
    const id = searchParams.get('id');
    if (typ && id) {
      setActiveTab('detail');
      loadDetail(typ, id);
    }
  }, [searchParams]);

  // Initial laden
  useEffect(() => {
    loadStats();
    loadBeziehungsarten();
  }, []);

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/statistiken`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const loadBeziehungsarten = async () => {
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/beziehungsarten`);
      const data = await res.json();
      setBeziehungsarten(data);
    } catch (error) {
      console.error('Fehler beim Laden der Beziehungsarten:', error);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const typParam = searchTypFilter !== 'alle' ? `&typ=${searchTypFilter}` : '';
      const res = await fetch(`${API_URL}/api/netzwerk/suche?q=${encodeURIComponent(searchTerm)}${typParam}`);
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch (error) {
      toast.error('Fehler bei der Suche');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, searchTypFilter]);

  // Debounce Suche
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchTypFilter, handleSearch]);

  const loadDetail = async (typ, id) => {
    setDetailLoading(true);
    setSelectedItem({ typ, id });
    
    try {
      let endpoint = '';
      if (typ === 'juristische_person') {
        endpoint = `/api/netzwerk/institution/${id}/betreute`;
      } else if (typ === 'natuerliche_person') {
        endpoint = `/api/netzwerk/person/${id}/faelle`;
      } else if (typ === 'klient') {
        endpoint = `/api/netzwerk/klient/${id}/netzwerk`;
      }
      
      const res = await fetch(`${API_URL}${endpoint}`);
      const data = await res.json();
      setDetailData(data);
      setActiveTab('detail');
    } catch (error) {
      toast.error('Fehler beim Laden der Details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResultClick = (result) => {
    setSearchParams({ typ: result.typ, id: result.id });
    loadDetail(result.typ, result.id);
  };

  const handleAutoImport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/auto-import`, { method: 'POST' });
      const data = await res.json();
      toast.success(`Import: ${data.gesamt_neu} neue Verknüpfungen erstellt`);
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Auto-Import');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/verknuepfungen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLink)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Fehler beim Erstellen');
      }
      
      toast.success('Verknüpfung erstellt');
      setShowNewLinkDialog(false);
      setNewLink({ quell_typ: '', quell_id: '', ziel_typ: '', ziel_id: '', beziehung_art: '', beziehung_details: '' });
      
      // Detail neu laden falls aktiv
      if (selectedItem) {
        loadDetail(selectedItem.typ, selectedItem.id);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getTypIcon = (typ) => {
    switch (typ) {
      case 'klient': return Users;
      case 'juristische_person': return Building2;
      case 'natuerliche_person': return UserCircle;
      default: return Link2;
    }
  };

  const getTypLabel = (typ) => {
    switch (typ) {
      case 'klient': return 'Betreuter';
      case 'juristische_person': return 'Institution';
      case 'natuerliche_person': return 'Person';
      default: return typ;
    }
  };

  const getTypColor = (typ) => {
    switch (typ) {
      case 'klient': return 'bg-blue-100 text-blue-700';
      case 'juristische_person': return 'bg-teal-100 text-teal-700';
      case 'natuerliche_person': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="netzwerk-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Network className="w-7 h-7 text-indigo-600" />
            Personen- & Institutionen-Netzwerk
          </h1>
          <p className="text-slate-500 mt-1">
            Vernetzungen zwischen Betreuten, Institutionen und Kontaktpersonen
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => openTab('/institutionen-hierarchie', { title: 'Institutionen-Hierarchie' })}
            data-testid="btn-institutionen-hierarchie"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Hierarchie
          </Button>
          <Button 
            variant="outline"
            onClick={handleAutoImport}
            disabled={loading}
            data-testid="btn-auto-import"
          >
            <Download className="w-4 h-4 mr-2" />
            Auto-Import
          </Button>
          <Button 
            onClick={() => setShowNewLinkDialog(true)}
            data-testid="btn-neue-verknuepfung"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Verknüpfung
          </Button>
        </div>
      </div>

      {/* Statistik-Karten */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSearchTypFilter('klient'); setActiveTab('suche'); }}>
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.klienten}</p>
              <p className="text-xs text-slate-500">Betreute</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSearchTypFilter('juristische_person'); setActiveTab('suche'); }}>
            <CardContent className="p-4 text-center">
              <Building2 className="w-8 h-8 mx-auto text-teal-600 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.juristische_personen}</p>
              <p className="text-xs text-slate-500">Institutionen</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSearchTypFilter('natuerliche_person'); setActiveTab('suche'); }}>
            <CardContent className="p-4 text-center">
              <UserCircle className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.natuerliche_personen}</p>
              <p className="text-xs text-slate-500">Personen</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Link2 className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.verknuepfungen}</p>
              <p className="text-xs text-slate-500">Verknüpfungen</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Briefcase className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-slate-800">{stats.massnahmen}</p>
              <p className="text-xs text-slate-500">Maßnahmen</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="suche" className="flex items-center gap-2" data-testid="tab-suche">
            <Search className="w-4 h-4" />
            Suche
          </TabsTrigger>
          <TabsTrigger value="graph" className="flex items-center gap-2" data-testid="tab-graph">
            <Share2 className="w-4 h-4" />
            Graph
          </TabsTrigger>
          <TabsTrigger value="detail" className="flex items-center gap-2" data-testid="tab-detail" disabled={!selectedItem}>
            <Eye className="w-4 h-4" />
            Detail
          </TabsTrigger>
        </TabsList>

        {/* Suche Tab */}
        <TabsContent value="suche" className="space-y-4">
          {/* Suchleiste */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Suche nach Name, Ort, Institution..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="input-suche"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
            <Select value={searchTypFilter} onValueChange={setSearchTypFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="select-typ-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Alle Typen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Typen</SelectItem>
                <SelectItem value="klient">Betreute</SelectItem>
                <SelectItem value="juristische_person">Institutionen</SelectItem>
                <SelectItem value="natuerliche_person">Personen</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadStats} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Suchergebnisse */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : searchResults.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {searchResults.map((result) => {
                  const Icon = getTypIcon(result.typ);
                  return (
                    <div
                      key={`${result.typ}-${result.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => handleResultClick(result)}
                      data-testid={`result-${result.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getTypColor(result.typ)}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {result.name}
                          </p>
                          <p className="text-sm text-slate-500 truncate">
                            {result.subtext}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className={getTypColor(result.typ)}>
                          {getTypLabel(result.typ)}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : searchTerm.length >= 2 ? (
              <div className="text-center py-12">
                <Search className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Keine Ergebnisse für "{searchTerm}"</p>
              </div>
            ) : (
              <div className="text-center py-12">
                <Network className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Geben Sie mindestens 2 Zeichen ein um zu suchen</p>
                <p className="text-sm text-slate-400 mt-2">
                  Oder klicken Sie auf eine Statistik-Karte oben
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Graph Tab */}
        <TabsContent value="graph" className="space-y-4">
          <NetzwerkGraph 
            onNodeClick={(node) => {
              // Optional: direkte Aktion bei Knoten-Klick
            }}
            onNavigate={(typ, id) => {
              setSearchParams({ typ, id });
              loadDetail(typ, id);
            }}
          />
        </TabsContent>

        {/* Detail Tab */}
        <TabsContent value="detail" className="space-y-4">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : detailData ? (
            <div className="space-y-6">
              {/* Detail Header */}
              {selectedItem?.typ === 'juristische_person' && detailData.institution && (
                <InstitutionDetail 
                  data={detailData} 
                  onNavigate={(typ, id) => {
                    setSearchParams({ typ, id });
                    loadDetail(typ, id);
                  }}
                  navigate={navigate}
                />
              )}
              {selectedItem?.typ === 'natuerliche_person' && detailData.person && (
                <PersonDetail 
                  data={detailData} 
                  onNavigate={(typ, id) => {
                    setSearchParams({ typ, id });
                    loadDetail(typ, id);
                  }}
                  navigate={navigate}
                />
              )}
              {selectedItem?.typ === 'klient' && detailData.klient && (
                <KlientNetzwerk 
                  data={detailData} 
                  onNavigate={(typ, id) => {
                    setSearchParams({ typ, id });
                    loadDetail(typ, id);
                  }}
                  navigate={navigate}
                />
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Eye className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Wählen Sie ein Element aus der Suche</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Neue Verknüpfung Dialog */}
      <NewLinkDialog 
        open={showNewLinkDialog}
        onClose={() => setShowNewLinkDialog(false)}
        newLink={newLink}
        setNewLink={setNewLink}
        beziehungsarten={beziehungsarten}
        onCreate={handleCreateLink}
      />
    </div>
  );
}

// ==================== SUB-KOMPONENTEN ====================

function InstitutionDetail({ data, onNavigate, navigate }) {
  const { institution, betreute, anzahl } = data;
  const [ansprechpartner, setAnsprechpartner] = useState([]);
  const [apLoading, setApLoading] = useState(true);

  useEffect(() => {
    if (!institution?.id) return;
    const loadAP = async () => {
      setApLoading(true);
      try {
        const token = localStorage.getItem('olympus_token');
        const res = await fetch(`${API_URL}/api/netzwerk/institution/${institution.id}/ansprechpartner`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const d = await res.json();
          setAnsprechpartner(d.ansprechpartner || []);
        }
      } catch (e) { console.error(e); }
      setApLoading(false);
    };
    loadAP();
  }, [institution?.id]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-8 h-8 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-800">{institution.name}</h2>
              <Badge variant="secondary" className="mt-1 bg-teal-100 text-teal-700">
                {institution.typ}
              </Badge>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                {institution.ort && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {institution.ort}
                  </span>
                )}
                {institution.telefon && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {institution.telefon}
                  </span>
                )}
                {institution.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {institution.email}
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/personen/juristisch/${institution.id}`)}>
              <Eye className="w-4 h-4 mr-2" />
              Öffnen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Ansprechpartner bei dieser Institution */}
      <Card className="border-purple-100" data-testid="ansprechpartner-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-purple-600" />
            Ansprechpartner ({ansprechpartner.length})
          </CardTitle>
          <CardDescription>
            Natürliche Personen die bei {institution.name} arbeiten
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apLoading ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : ansprechpartner.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {ansprechpartner.map((ap) => (
                <div key={ap.id} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between">
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:text-purple-600"
                    onClick={() => onNavigate('natuerliche_person', ap.id)}
                  >
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                      <UserCircle className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{ap.vorname} {ap.nachname}</p>
                      <p className="text-xs text-slate-500">{ap.rolle || 'Kontaktperson'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {ap.telefon && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {ap.telefon}</span>}
                    {ap.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {ap.email}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-6 text-sm">
              Keine Ansprechpartner hinterlegt
            </p>
          )}
        </CardContent>
      </Card>

      {/* Betreute Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Verknüpfte Betreute ({anzahl})
          </CardTitle>
          <CardDescription>
            Alle Betreuten die mit dieser Institution verbunden sind
          </CardDescription>
        </CardHeader>
        <CardContent>
          {betreute.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {betreute.map(({ klient, beziehungen }) => (
                <div key={klient.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-blue-600"
                      onClick={() => onNavigate('klient', klient.id)}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {klient.vorname} {klient.nachname}
                        </p>
                        <p className="text-sm text-slate-500">
                          {klient.fallnummer || 'Keine Fallnummer'} · {klient.ort || ''}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/akten/${klient.id}`)}>
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2 ml-13 flex flex-wrap gap-2">
                    {beziehungen.map((b, idx) => {
                      const Icon = BEZIEHUNG_ICONS[b.art] || Link2;
                      return (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className={`${BEZIEHUNG_FARBEN[b.art] || 'bg-gray-100 text-gray-700'} flex items-center gap-1`}
                        >
                          <Icon className="w-3 h-3" />
                          {b.label}
                          {b.details && <span className="text-xs opacity-75">({b.details})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              Keine Betreuten mit dieser Institution verknüpft
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PersonDetail({ data, onNavigate, navigate }) {
  const { person, faelle, anzahl } = data;
  const [firmaName, setFirmaName] = useState(null);

  useEffect(() => {
    if (!person?.firma_id) { setFirmaName(null); return; }
    const load = async () => {
      try {
        const token = localStorage.getItem('olympus_token');
        const res = await fetch(`${API_URL}/api/netzwerk/resolve-entities?ids=${person.firma_id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const d = await res.json();
          const resolved = d[person.firma_id];
          if (resolved) setFirmaName(resolved);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [person?.firma_id]);
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
              <UserCircle className="w-8 h-8 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-800">
                {person.vorname} {person.nachname}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                  {person.rolle}
                </Badge>
                {firmaName && (
                  <Badge
                    variant="secondary"
                    className="bg-teal-100 text-teal-700 cursor-pointer hover:bg-teal-200 transition-colors"
                    onClick={() => onNavigate('juristische_person', person.firma_id)}
                    data-testid="person-firma-badge"
                  >
                    <Building2 className="w-3 h-3 mr-1" />
                    Ansprechpartner bei {firmaName.name}
                  </Badge>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                {person.telefon && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {person.telefon}
                  </span>
                )}
                {person.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {person.email}
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/personen/natuerlich/${person.id}`)}>
              <Eye className="w-4 h-4 mr-2" />
              Öffnen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fälle Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Verknüpfte Fälle ({anzahl})
          </CardTitle>
          <CardDescription>
            Alle Fälle/Betreute wo diese Person vorkommt
          </CardDescription>
        </CardHeader>
        <CardContent>
          {faelle.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {faelle.map(({ klient, beziehungen }) => (
                <div key={klient.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-blue-600"
                      onClick={() => onNavigate('klient', klient.id)}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {klient.vorname} {klient.nachname}
                        </p>
                        <p className="text-sm text-slate-500">
                          {klient.fallnummer || 'Keine Fallnummer'} · {klient.ort || ''}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/akten/${klient.id}`)}>
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2 ml-13 flex flex-wrap gap-2">
                    {beziehungen.map((b, idx) => {
                      const Icon = BEZIEHUNG_ICONS[b.art] || Link2;
                      return (
                        <Badge 
                          key={idx} 
                          variant="secondary" 
                          className={`${BEZIEHUNG_FARBEN[b.art] || 'bg-gray-100 text-gray-700'} flex items-center gap-1`}
                        >
                          <Icon className="w-3 h-3" />
                          {b.label}
                          {b.details && <span className="text-xs opacity-75">({b.details})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              Keine Fälle mit dieser Person verknüpft
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KlientNetzwerk({ data, onNavigate, navigate }) {
  const { klient, institutionen, personen, statistik } = data;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-800">
                {klient.vorname} {klient.nachname}
              </h2>
              <p className="text-slate-500 mt-1">
                Fallnummer: {klient.fallnummer || 'k.A.'} · Status: {klient.status}
              </p>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                {klient.ort && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {klient.ort}
                  </span>
                )}
                {klient.telefon && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {klient.telefon}
                  </span>
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate(`/akten/${klient.id}`)}>
              <Eye className="w-4 h-4 mr-2" />
              Akte öffnen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistik */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Building2 className="w-6 h-6 mx-auto text-teal-600 mb-1" />
            <p className="text-xl font-bold">{statistik.institutionen_anzahl}</p>
            <p className="text-xs text-slate-500">Institutionen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <UserCircle className="w-6 h-6 mx-auto text-purple-600 mb-1" />
            <p className="text-xl font-bold">{statistik.personen_anzahl}</p>
            <p className="text-xs text-slate-500">Personen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Briefcase className="w-6 h-6 mx-auto text-green-600 mb-1" />
            <p className="text-xl font-bold">{statistik.massnahmen_anzahl}</p>
            <p className="text-xs text-slate-500">Maßnahmen</p>
          </CardContent>
        </Card>
      </div>

      {/* Institutionen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" />
            Institutionen ({institutionen.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {institutionen.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {institutionen.map(({ institution, beziehungen }, idx) => (
                <div key={`${institution.id}-${idx}`} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-teal-600"
                      onClick={() => onNavigate('juristische_person', institution.id)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{institution.name}</p>
                        <p className="text-sm text-slate-500">{institution.typ} · {institution.ort || ''}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/personen/juristisch/${institution.id}`)}>
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2 ml-13 flex flex-wrap gap-2">
                    {beziehungen.map((b, bidx) => {
                      const Icon = BEZIEHUNG_ICONS[b.art] || Link2;
                      return (
                        <Badge 
                          key={bidx} 
                          variant="secondary" 
                          className={`${BEZIEHUNG_FARBEN[b.art] || 'bg-gray-100 text-gray-700'} flex items-center gap-1`}
                        >
                          <Icon className="w-3 h-3" />
                          {b.label}
                          {b.details && <span className="text-xs opacity-75">({b.details})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">Keine Institutionen verknüpft</p>
          )}
        </CardContent>
      </Card>

      {/* Personen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-purple-600" />
            Personen ({personen.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {personen.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {personen.map(({ person, beziehungen }, idx) => (
                <div key={`${person.id}-${idx}`} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer hover:text-purple-600"
                      onClick={() => onNavigate('natuerliche_person', person.id)}
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <UserCircle className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{person.vorname} {person.nachname}</p>
                        <p className="text-sm text-slate-500">{person.rolle}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/personen/natuerlich/${person.id}`)}>
                      <ArrowUpRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-2 ml-13 flex flex-wrap gap-2">
                    {beziehungen.map((b, bidx) => {
                      const Icon = BEZIEHUNG_ICONS[b.art] || Link2;
                      return (
                        <Badge 
                          key={bidx} 
                          variant="secondary" 
                          className={`${BEZIEHUNG_FARBEN[b.art] || 'bg-gray-100 text-gray-700'} flex items-center gap-1`}
                        >
                          <Icon className="w-3 h-3" />
                          {b.label}
                          {b.details && <span className="text-xs opacity-75">({b.details})</span>}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">Keine Personen verknüpft</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NewLinkDialog({ open, onClose, newLink, setNewLink, beziehungsarten, onCreate }) {
  const [searchSource, setSearchSource] = useState('');
  const [searchTarget, setSearchTarget] = useState('');
  const [sourceResults, setSourceResults] = useState([]);
  const [targetResults, setTargetResults] = useState([]);
  const [loadingSource, setLoadingSource] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);

  const searchEntities = async (term, setResults, setLoading, typ) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const typParam = typ !== 'alle' ? `&typ=${typ}` : '';
      const res = await fetch(`${API_URL}/api/netzwerk/suche?q=${encodeURIComponent(term)}${typParam}`);
      const data = await res.json();
      setResults(data.results || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchSource) {
        searchEntities(searchSource, setSourceResults, setLoadingSource, newLink.quell_typ || 'alle');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchSource, newLink.quell_typ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTarget) {
        searchEntities(searchTarget, setTargetResults, setLoadingTarget, newLink.ziel_typ || 'alle');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTarget, newLink.ziel_typ]);

  const selectSource = (result) => {
    setNewLink(prev => ({ ...prev, quell_typ: result.typ, quell_id: result.id }));
    setSearchSource(result.name);
    setSourceResults([]);
  };

  const selectTarget = (result) => {
    setNewLink(prev => ({ ...prev, ziel_typ: result.typ, ziel_id: result.id }));
    setSearchTarget(result.name);
    setTargetResults([]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Neue Verknüpfung erstellen</DialogTitle>
          <DialogDescription>
            Verknüpfen Sie Betreute, Institutionen und Personen miteinander.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Quelle */}
          <div>
            <label className="text-sm font-medium text-slate-700">Quelle</label>
            <div className="relative mt-1">
              <Input
                placeholder="Suche nach Quelle..."
                value={searchSource}
                onChange={(e) => setSearchSource(e.target.value)}
                data-testid="input-quelle"
              />
              {sourceResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {sourceResults.map((r) => (
                    <div
                      key={`${r.typ}-${r.id}`}
                      className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                      onClick={() => selectSource(r)}
                    >
                      <span className="font-medium">{r.name}</span>
                      <span className="text-slate-400 ml-2">({r.typ})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {newLink.quell_id && (
              <Badge variant="secondary" className="mt-2">{newLink.quell_typ}: {searchSource}</Badge>
            )}
          </div>

          {/* Ziel */}
          <div>
            <label className="text-sm font-medium text-slate-700">Ziel</label>
            <div className="relative mt-1">
              <Input
                placeholder="Suche nach Ziel..."
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
                data-testid="input-ziel"
              />
              {targetResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                  {targetResults.map((r) => (
                    <div
                      key={`${r.typ}-${r.id}`}
                      className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm"
                      onClick={() => selectTarget(r)}
                    >
                      <span className="font-medium">{r.name}</span>
                      <span className="text-slate-400 ml-2">({r.typ})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {newLink.ziel_id && (
              <Badge variant="secondary" className="mt-2">{newLink.ziel_typ}: {searchTarget}</Badge>
            )}
          </div>

          {/* Beziehungsart */}
          <div>
            <label className="text-sm font-medium text-slate-700">Beziehungsart</label>
            <Select 
              value={newLink.beziehung_art} 
              onValueChange={(v) => setNewLink(prev => ({ ...prev, beziehung_art: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="select-beziehung">
                <SelectValue placeholder="Art der Beziehung wählen" />
              </SelectTrigger>
              <SelectContent>
                {beziehungsarten.map((b) => (
                  <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Details */}
          <div>
            <label className="text-sm font-medium text-slate-700">Details (optional)</label>
            <Input
              className="mt-1"
              placeholder="z.B. KV-Nr, Aktenzeichen..."
              value={newLink.beziehung_details}
              onChange={(e) => setNewLink(prev => ({ ...prev, beziehung_details: e.target.value }))}
              data-testid="input-details"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button 
            onClick={onCreate}
            disabled={!newLink.quell_id || !newLink.ziel_id || !newLink.beziehung_art}
            data-testid="btn-verknuepfung-erstellen"
          >
            Verknüpfung erstellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
